import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useRef, type ChangeEvent } from 'react';
import { useIntl } from 'react-intl';

import { ConceptsFileUpload } from '@verantos-dev/feature-concept-groups-list';
import {
  ApiWorkflowDialog,
  type ApiWorkflowDialogMessages,
  Icon,
} from '@verantos-dev/shared-components';

import {
  CONCEPT_GROUP_NAME_MAX_LENGTH,
  useCreateConceptGroupModal,
  type CreatedConceptGroupDraft,
} from '../hooks/useCreateConceptGroupModal';

interface CreateConceptGroupModalProps {
  projectId: string;
  onClose: () => void;
  /** Fired with the minted draft's ids before close so the caller can route
   *  into the draft review page. */
  onCreated: (created: CreatedConceptGroupDraft) => void;
}

/**
 * Gen-3 inline "Create concept group" dialog opened from the artifacts page
 * Create menu (replaces the in-page stepper entry; the flag-OFF path keeps the
 * legacy ConceptGroupWorkflowDialog). Single step: name + spreadsheet — Create
 * mints the draft via the sync upload and the caller routes into the draft
 * review page, where mapping review takes over.
 *
 * Reuses the standalone modal's atoms: the ApiWorkflowDialog frame (same as
 * CreateAnalyticalDatasetDialog beside it), ConceptsFileUpload (dropzone +
 * file row), and the `conceptGroup.*` / `epiChat.conceptGroupPreview.step1.*`
 * message ids so copy stays in sync with the existing surfaces.
 *
 */
export const CreateConceptGroupModal = ({
  projectId,
  onClose,
  onCreated,
}: CreateConceptGroupModalProps) => {
  const { formatMessage } = useIntl();
  const {
    name,
    setName,
    file,
    setFile,
    nameStatus,
    onSubmit,
    closeDialog,
    isPending,
    submittingError,
    isValidToSubmit,
  } = useCreateConceptGroupModal({ projectId, onClose, onCreated });

  // Recovery from a failed upload (e.g. 422 invalid content): the error step's
  // primary becomes "Choose a different file", which opens the OS picker and
  // re-uploads with the new file — no dead-end retry of the same bad file. The
  // input lives OUTSIDE the dialog content (the error step doesn't render the
  // form) so it's always in the DOM to be clicked.
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const openReplacePicker = () => replaceInputRef.current?.click();
  const onReplacePicked = (event: ChangeEvent<HTMLInputElement>) => {
    const picked = event.target.files?.[0];
    event.target.value = '';
    if (picked) {
      setFile(picked);
      onSubmit(picked);
    }
  };

  const dialogMessages: ApiWorkflowDialogMessages = {
    entryCancelButtonText: formatMessage({ id: 'conceptGroup.dialog.cancel' }),
    entrySaveButtonText: formatMessage({ id: 'conceptGroups.actions.createConceptGroup' }),
    submittingCancelButtonText: formatMessage({ id: 'conceptGroup.dialog.cancel' }),
    submittingSaveButtonText: formatMessage({ id: 'conceptGroups.actions.createConceptGroup' }),
    submittingErrorTitle: formatMessage({ id: 'conceptGroup.error.title' }),
    // Error step: swap the dead-end "Retry" for a "Choose a different file" action.
    submittingErrorSaveButtonText: formatMessage({ id: 'conceptGroup.dialog.chooseDifferentFile' }),
  };

  return (
    <>
      {/* Always mounted (outside the dialog content) so the error step's
          "Choose a different file" can open it even though the form isn't shown. */}
      <input
        ref={replaceInputRef}
        type="file"
        accept=".csv,.xlsx,.xls"
        data-testid="cg-create-modal-replace-input"
        onChange={onReplacePicked}
        hidden
      />
      <ApiWorkflowDialog
        id="createConceptGroupModal"
        title={formatMessage({ id: 'conceptGroup.dialog.title' })}
        dialogMessages={dialogMessages}
        onClose={closeDialog}
        onSubmit={onSubmit}
        // Error step primary re-opens the picker (Choose a different file) rather
        // than retrying the same rejected file.
        onRetrySubmit={openReplacePicker}
        isValidForSubmit={isValidToSubmit}
        isSubmitting={isPending}
        submittingError={submittingError}
        hideDisplayButtonsOnSuccess
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, pt: 1 }}>
          <TextField
            size="small"
            // No floating label: it rested over the empty field (hiding the
            // "Enter concept group name" placeholder) and its first letter clipped
            // at the rounded corner when floated. The placeholder is the prompt;
            // aria-label keeps the accessible name.
            placeholder={formatMessage({ id: 'epiChat.conceptGroupPreview.step1.namePlaceholder' })}
            value={name}
            // Hard cap the name at the limit. maxLength blocks typing/paste
            // natively; the slice also covers drag-dropped text, which bypasses
            // maxLength. The counter therefore never exceeds the max.
            onChange={(event) =>
              setName(event.target.value.slice(0, CONCEPT_GROUP_NAME_MAX_LENGTH))
            }
            error={nameStatus === 'taken'}
            helperText={
              nameStatus === 'taken'
                ? formatMessage({ id: 'epiChat.conceptGroupPreview.step1.nameExists' })
                : formatMessage(
                    { id: 'epiChat.conceptGroupPreview.step1.charCount' },
                    { count: name.length, max: CONCEPT_GROUP_NAME_MAX_LENGTH }
                  )
            }
            inputProps={{
              maxLength: CONCEPT_GROUP_NAME_MAX_LENGTH,
              'data-testid': 'cg-create-modal-name-input',
              'aria-label': formatMessage({ id: 'epiChat.conceptGroupPreview.step1.nameLabel' }),
            }}
            InputProps={{
              endAdornment:
                nameStatus === 'checking' ? (
                  <InputAdornment position="end">
                    <CircularProgress size={16} data-testid="cg-create-modal-name-checking" />
                  </InputAdornment>
                ) : undefined,
            }}
          />
          {file ? (
            // Figma: once a file is picked the dropzone goes away — just the
            // file row with a single ✕. The name wraps on its own flex track so
            // long filenames never push the ✕ out of place.
            <Paper
              variant="outlined"
              data-testid="cg-create-modal-file-row"
              sx={{
                py: 2,
                px: 4,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                borderRadius: 1,
                bgcolor: 'grey.50',
              }}
            >
              <Icon
                id="cg-create-modal-file-icon"
                icon="file"
                width={24}
                height={24}
                sx={{ flexShrink: 0 }}
              />
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}
              >
                {file.name}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setFile(null)}
                disabled={isPending}
                data-testid="cg-create-modal-file-remove"
                aria-label={formatMessage({ id: 'epiChat.conceptGroupPreview.step1.removeFile' })}
                sx={{ flexShrink: 0 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Paper>
          ) : (
            <ConceptsFileUpload selectedFile={null} onFileSelect={setFile} isLoading={isPending} />
          )}
        </Box>
      </ApiWorkflowDialog>
    </>
  );
};
